import { useDebounced } from '@finn-no/search-frontend-components/react';
import { Combobox } from '@headlessui/react';
import React from 'react';
import { SearchboxButton } from './SearchboxButton';
import { SearchboxDialog } from './SearchboxDialog';
import { SearchboxInput } from './SearchboxInput';
import { SearchboxList } from './SearchboxList';
import { SearchboxPopover } from './SearchboxPopover';
import { useClickOutside } from './useClickOutside';

type SearchboxProps<TValue> = {
    accessibilityHeading?: () => React.ReactNode;
    children?: React.ReactNode;
    defaultValue?: TValue;
    defaultOption?: () => React.ReactNode;
    displayValue?: (item?: TValue) => string;
    isModal?: boolean;
    isStatic?: boolean;
    name?: string;
    onChange: (value: string) => void;
    onFocus?: () => void;
    onSelect: (value: TValue) => boolean;
    openOnFocus?: boolean;
    placeholder?: string;
};

export function Searchbox<TValue>(props: SearchboxProps<TValue>) {
    const {
        accessibilityHeading,
        children,
        defaultValue,
        defaultOption,
        displayValue,
        isModal = false,
        isStatic = false,
        name,
        onChange,
        onFocus,
        onSelect,
        openOnFocus = false,
        placeholder,
    } = props;

    const inputRef = React.useRef<HTMLInputElement>(null);

    const [isFocused, setIsFocused] = React.useState(false);

    const handleFocus = React.useCallback(() => {
        onFocus && onFocus();
        openOnFocus && setIsFocused(true);
    }, [onFocus, openOnFocus, setIsFocused]);

    const handleModalDismiss = React.useCallback(() => {
        inputRef.current?.blur();
        onChange('');
        setIsFocused(false);
    }, [inputRef, onChange, setIsFocused]);

    const containerRef = useClickOutside<HTMLDivElement>(
        React.useCallback(() => {
            setIsFocused(false);
        }, [setIsFocused]),
    );

    const debouncedOnChange = useDebounced(onChange, 100) as (
        value: string,
    ) => void;

    const handleSelect = React.useCallback(
        (value: TValue) => {
            if (value) {
                const keepOpen = onSelect && onSelect(value);
                setIsFocused(keepOpen);
            }
        },
        [onSelect, setIsFocused],
    );

    const memoizedAccessibilityHeading = React.useMemo(() => {
        return accessibilityHeading ? accessibilityHeading() : null;
    }, [accessibilityHeading]);

    const memoizedDefaultOption = React.useMemo(() => {
        return defaultOption ? defaultOption() : null;
    }, [defaultOption]);

    return (
        <Combobox
            as="div"
            className="searchbox"
            defaultValue={defaultValue}
            nullable
            onChange={handleSelect}
            ref={containerRef}
        >
            {({ open }) => {
                const isOpen = isStatic || (openOnFocus ? isFocused : open);
                const isModalOpen = isModal && isOpen;
                return (
                    <SearchboxDialog
                        isModal={isModal}
                        isOpen={isOpen}
                        onDismiss={handleModalDismiss}
                    >
                        <SearchboxInput
                            displayValue={displayValue}
                            name={name}
                            onChange={debouncedOnChange}
                            onFocus={handleFocus}
                            placeholder={placeholder}
                            ref={inputRef}
                        >
                            {!isModalOpen && <SearchboxButton />}
                        </SearchboxInput>
                        <SearchboxPopover
                            isModal={isModal}
                            isOpen={isOpen}
                            isStatic={isStatic}
                        >
                            {memoizedAccessibilityHeading}
                            <SearchboxList>
                                {memoizedDefaultOption}
                                {children}
                            </SearchboxList>
                        </SearchboxPopover>
                    </SearchboxDialog>
                );
            }}
        </Combobox>
    );
}
